"use client";

import React from "react";
import { HelpCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import { useParams, useRouter } from "next/navigation";
import { useUser } from "@/context/UserProvider";
import { routing } from "@/i18n/routing";
import PacksGrid from "@/components/billing/PacksGrid";
import { useBillingPacks } from "@/components/billing/useBillingPacks";


const LandingPricingPage = () => {
    const t = useTranslations();
    const { isLoggedIn } = useUser();
    const router = useRouter();
    const params = useParams<{ locale?: string }>();
    const locale = (params?.locale as (typeof routing.locales)[number]) ?? routing.defaultLocale;
    const { packs, isLoading } = useBillingPacks(locale);

    const handleSelectPack = () => {
        const billingPath = `/${locale}/billing/tokens`;
        if (!isLoggedIn) {
            sessionStorage.setItem("intendedPath", billingPath);
            router.push(`/${locale}/login`);
            return;
        }
        router.push(billingPath);
    };

    return (
        <main className="flex-1 pt-32 pb-24 bg-background">
            <div className="container-std max-w-7xl mx-auto px-6">
                <div className="text-center mb-16 max-w-3xl mx-auto">
                    <span className="text-primary font-bold tracking-widest uppercase text-xs mb-3 block">{t('pricing_subtitle')}</span>
                    <h1 className="text-4xl md:text-6xl font-black text-foreground mb-6 tracking-tight font-sans">
                        {t('pricing_title')}
                    </h1>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                        {t('pricing_subtitle')}
                    </p>
                </div>

                <PacksGrid
                    packs={packs}
                    isLoading={isLoading}
                    onSelect={handleSelectPack}
                    variant="landing"
                    ctaLabel={(pack, idx, isPopular) => {
                        if (isPopular) return `${t("try_plan")} ${pack.name}`;
                        return `${t("select_plan")} ${pack.name}`;
                    }}
                />

                {/* FAQ Section */}
                <div className="max-w-3xl mx-auto mt-20">
                    <h2 className="text-2xl font-bold text-foreground mb-8 text-center">{t('faq')}</h2>
                    <div className="space-y-4">
                        {[
                            { q: t('faq_1_q'), a: t('faq_1_a') },
                            { q: t('faq_2_q'), a: t('faq_2_a') },
                            { q: t('faq_3_q'), a: t('faq_3_a') }
                        ].map((item, i) => (
                            <div key={i} className="glass-card p-6 rounded-2xl border border-white/10">
                                <h3 className="font-bold text-foreground flex items-start gap-3">
                                    <HelpCircle size={20} className="text-primary shrink-0 mt-0.5" />
                                    {item.q}
                                </h3>
                                <p className="mt-3 text-muted-foreground ml-8 leading-relaxed">
                                    {item.a}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </main>
    );
};

export default LandingPricingPage;
